'use client';

import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { resolveLanguage, translate, type TranslationKey } from '../i18n';
import { reportError } from '../monitoring/reportError';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    reportError(error);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  /**
   * Class components cannot call hooks, and the boundary must keep working
   * even if the store failed, so read the persisted locale directly.
   */
  private t(key: TranslationKey): string {
    return translate(resolveLanguage(), key);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-hc-surface flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            <div className="mx-auto h-20 w-20 bg-hc-accent/15 rounded-full flex items-center justify-center mb-6">
              <AlertTriangle className="h-10 w-10 text-hc-accent-soft" />
            </div>

            <h1 className="text-2xl font-bold text-white mb-4">
              {this.t('error.boundary.title')}
            </h1>

            <p className="text-white/60 mb-6">
              {this.t('error.boundary.body')}
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-6 text-start">
                <summary className="cursor-pointer text-sm font-medium text-white/70 mb-2">
                  {this.t('error.boundary.devDetails')}
                </summary>
                <div className="bg-white/[0.06] p-4 rounded-tile text-xs font-mono text-white/90 overflow-auto">
                  <div className="mb-2">
                    <strong>{this.t('error.boundary.errorLabel')}</strong>{' '}
                    {this.state.error.toString()}
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong>{this.t('error.boundary.stackLabel')}</strong>
                      <pre className="mt-1 whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-6 py-3 bg-hc-accent hover:bg-hc-accent-hover text-hc-cream text-cta font-display font-extrabold rounded-pill focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hc-accent disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-glow"
              >
                <RefreshCw className="h-4 w-4 me-2 inline" />
                {this.t('error.boundary.tryAgain')}
              </button>

              <button
                onClick={this.handleGoHome}
                className="px-6 py-3 bg-white/[0.08] text-white font-medium rounded-pill border border-white/[0.13] hover:bg-white/[0.13] focus:outline-none focus:ring-2 focus:ring-white/25 transition-all duration-200 shadow-lg"
              >
                <Home className="h-4 w-4 me-2 inline" />
                {this.t('error.boundary.goHome')}
              </button>
            </div>

            <div className="mt-6 text-sm text-white/45">
              <p>{this.t('error.boundary.persists')}</p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
